import React, { useEffect, useMemo, useState } from "react";

import { FormBuilder } from "shared/components";
import { IAPIError, IFormButtons } from "shared/types";

import { customAccommodationRoomTypesSelectorCreator } from "Bands/state";
import { customAccommodationRoomsSelectorCreator } from "Bands/state/data/selectors/accommodationRoom";
import {
  AccommodationRoomType,
  IAccommodationReservationFormParams,
  IAccommodationRoomType,
  IAccommodationSingleReservationFormParams,
} from "Bands/types";
import { useSelector } from "react-redux";
import { copy } from "shared/assets";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { Spacer } from "shared/layout";
import { BoldText, FlexContainer, MutedDarkText, SmallText } from "shared/styles";
import { getFields } from "./formData";
import { StyledAccommodationRoomTypeSelectOption } from "./styles";

interface Props {
  isSubmitting?: boolean;
  error?: string | boolean;
  buttons?: IFormButtons;
  apiError?: IAPIError | null;
  defaultValues?: IAccommodationSingleReservationFormParams;
  handleSubmit: (data: any) => void;
  blockId: string;
  isEdit: boolean;
  currentRoom?: string;
}

const { accommodationBlock: blockCopy } = copy;

export const AccommodationReservationForm: React.FC<Props> = ({
  buttons,
  handleSubmit,
  error,
  isSubmitting,
  defaultValues,
  apiError,
  blockId,
  isEdit = false,
  currentRoom,
}) => {
  const [nbPeople, setNbPeople] = useState<number>(isEdit ? 1 : 0);
  const [selectedRoomType, setSelectedRoomType] = useState(
    defaultValues?.roomType ? defaultValues.roomType : ""
  );

  const [forceUpdateValues, setForceUpdateValues] = useState<
    IAccommodationReservationFormParams | undefined
  >();

  const roomTypesSelector = useMemo(
    () => customAccommodationRoomTypesSelectorCreator(blockId),
    [blockId]
  );

  const roomsSelector = useMemo(
    () => customAccommodationRoomsSelectorCreator(selectedRoomType, nbPeople, currentRoom),
    [selectedRoomType, nbPeople, currentRoom]
  );

  const accommodationRoomTypes = useSelector(roomTypesSelector);
  const availableRooms = useSelector(roomsSelector);

  const availableRoomTypes: ISelectOption[] = useMemo((): ISelectOption[] => {
    return accommodationRoomTypes.map(
      ({ id, name, peoplePerRoom, vacancy }: IAccommodationRoomType) => {
        return {
          labelComponent: (
            <StyledAccommodationRoomTypeSelectOption>
              <Spacer size="xxs">
                <BoldText as={!vacancy || vacancy < nbPeople ? MutedDarkText : BoldText}>
                  {AccommodationRoomType[name as keyof typeof AccommodationRoomType] || name}
                </BoldText>
                <div>
                  <FlexContainer size="xxxs">
                    <SmallText as={MutedDarkText}>
                      <BoldText>{peoplePerRoom}</BoldText> {peoplePerRoom > 1 ? "beds" : "bed"}
                      /room •
                    </SmallText>
                    <SmallText as={MutedDarkText}>
                      <BoldText>{vacancy}</BoldText> free {vacancy === 1 ? "bed" : "beds"} left
                    </SmallText>
                  </FlexContainer>
                </div>
              </Spacer>
            </StyledAccommodationRoomTypeSelectOption>
          ),
          value: id,
          label: AccommodationRoomType[name as keyof typeof AccommodationRoomType] || name,
          isDisabled: !vacancy || vacancy < nbPeople,
        };
      }
    );
  }, [accommodationRoomTypes, nbPeople]);

  useEffect(() => {
    if (availableRooms.length === 1) {
      setForceUpdateValues({
        room: availableRooms[0].id,
      });
    } else {
      const found = availableRooms.find(room => room.id === currentRoom);

      setForceUpdateValues({
        room: (found && found.id) || undefined,
      });
    }
  }, [availableRooms, selectedRoomType, currentRoom]);

  const selectedRoomTypeLabel = useMemo(() => {
    const type = accommodationRoomTypes.find(roomType => roomType.id === selectedRoomType);

    return (
      AccommodationRoomType[type?.name as keyof typeof AccommodationRoomType] || type?.name || ""
    );
  }, [accommodationRoomTypes, selectedRoomType]);

  const globalInfo = useMemo(() => {
    if (!selectedRoomType || !nbPeople) {
      return "";
    }

    const vacancy = accommodationRoomTypes.find(
      roomType => roomType.id === selectedRoomType
    )?.vacancy;

    if (vacancy && vacancy >= nbPeople && availableRooms.length === 0) {
      return blockCopy.create.peopleBlock.errors.autoAssignRooms;
    }

    if (vacancy && vacancy < nbPeople) {
      return blockCopy.create.peopleBlock.errors.notAvailable;
    }
  }, [nbPeople, availableRooms, selectedRoomType, accommodationRoomTypes]);

  return (
    <FormBuilder
      isSubmitting={isSubmitting}
      apiErrorMessage={error}
      apiError={apiError}
      fields={getFields({
        availableRoomTypes,
        availableRooms,
        setNbPeople,
        selectedRoomType,
        setSelectedRoomType,
        isEdit,
        selectedRoomTypeLabel,
        currentRoom,
      })}
      forceUpdateValues={forceUpdateValues}
      onSubmit={handleSubmit}
      submitMode="isDirty"
      buttons={buttons}
      globalInfo={globalInfo}
      defaultValues={defaultValues}
      style={{ gridTemplateColumns: "repeat(6, 1fr)" }}
    />
  );
};
